import { createFileRoute } from '@tanstack/react-router';
import { Inbox } from '@osd/icons/inbox';
import { Bell } from '@osd/icons/bell';
import { Mail } from '@osd/icons/mail';
import { Badge } from '@osd/ui/badge';
import { Button } from '@osd/ui/button';
import { Card } from '@osd/ui/card';
import { Stack } from '@osd/ui/stack';
import { Table } from '@osd/ui/table';
import { Text } from '@osd/ui/text';
import { CodeBlock } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';

/**
 * /ui/badge — live docs for the `Badge` primitive.
 *
 * Badge is the non-semantic numerical indicator — inbox counts,
 * notification counters, anything where *the number is the
 * meaning*. For semantic status / role chips use `<Pill>`; for
 * quiet monochrome metadata use `<Label>`.
 */
export const Route = createFileRoute('/ui/_layout/badge')({
  component: BadgePage,
});

function BadgePage() {
  return (
    <Stack gap={6}>
      <Section
        title="Badge"
        hint="Small inline numerical indicator — 2 sizes × overflow (`99+`) × optional ariaLabel"
      >
        <Text>
          Badge renders as a <code>&lt;span&gt;</code> wrapping a <code>&lt;output&gt;</code> (the
          count itself). Non-interactive by default — wrap in a <code>&lt;button&gt;</code> or{' '}
          <code>&lt;Link&gt;</code> for clickable cases. The whole point is small + quiet chrome —
          for colored status chips use <code>&lt;Pill&gt;</code>; for monochrome metadata prefixes
          use <code>&lt;Label&gt;</code>.
        </Text>
      </Section>

      <Section title="When to use which?" hint="Badge vs Pill vs Label — decision matrix">
        <Table columns="1fr 2fr 2fr">
          <Table.Header>
            <Table.Row>
              <Table.Head>Primitive</Table.Head>
              <Table.Head>Use when…</Table.Head>
              <Table.Head>Example</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>
                <strong>Badge</strong>
              </Table.Cell>
              <Table.Cell>
                Non-semantic numerical count — inbox indicators, notification counters. The number
                is the meaning.
              </Table.Cell>
              <Table.Cell>
                <code>{'<Badge value={3} />'}</code> next to a nav item
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <strong>Pill</strong>
              </Table.Cell>
              <Table.Cell>
                Semantic status / role / severity. Choose the variant by meaning.
              </Table.Cell>
              <Table.Cell>
                <code>{'<Pill variant="success">active</Pill>'}</code>
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <strong>Label</strong>
              </Table.Cell>
              <Table.Cell>
                Quiet monochrome metadata — categories, prefixes, dates. No semantic color.
              </Table.Cell>
              <Table.Cell>
                <code>{'<Label icon={<TagIcon />}>bug</Label>'}</code>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </Section>

      <Section title="Basic usage" hint="value + (optional) max">
        <Stack direction="row" gap={3} align="center">
          <Badge value={1} />
          <Badge value={9} />
          <Badge value={42} />
          <Badge value={100} />
          <Badge value={1500} />
        </Stack>
      </Section>

      <Section title="Sizes" hint="sm / md">
        <Stack direction="row" gap={3} align="center">
          <Badge value={1} size="sm" />
          <Badge value={42} size="sm" />
          <Badge value={1} size="md" />
          <Badge value={42} size="md" />
        </Stack>
      </Section>

      <Section title="Overflow" hint="Default max is 99 — anything higher truncates">
        <Stack gap={3}>
          <Text>
            When <code>value {'>'} max</code>, the badge renders <code>{'{max}+'}</code> so the
            surface stays narrow even with very large counts. Default max is <code>99</code> (so 100
            → "99+"); pass <code>max={'{999}'}</code> for higher ceilings or{' '}
            <code>max={'{Infinity}'}</code> to disable truncation entirely.
          </Text>
          <Stack direction="row" gap={3} align="center">
            <Badge value={99} />
            <Badge value={100} />
            <Badge value={999} />
            <Badge value={1000} max={999} />
            <Badge value={50000} max={Infinity} />
          </Stack>
        </Stack>
      </Section>

      <Section title="In a button" hint="Wrap the Badge in a <button> for clickable cases">
        <Card>
          <Card.Body>
            <Stack direction="row" gap={3}>
              <Button variant="ghost">
                <Inbox /> Inbox <Badge value={12} ariaLabel="12 unread" />
              </Button>
              <Button variant="ghost">
                <Bell /> Notifications <Badge value={3} ariaLabel="3 new" />
              </Button>
              <Button variant="ghost">
                <Mail /> Messages <Badge value={99} ariaLabel="99 messages" />
              </Button>
              <Button variant="ghost">
                Inbox <Badge value={1500} ariaLabel="1500 messages" />
              </Button>
            </Stack>
          </Card.Body>
        </Card>
      </Section>

      <Section
        title="With a leading label or icon"
        hint="Optional `children` prepends to the count"
      >
        <Stack direction="row" gap={3} align="center">
          <Badge value={3}>
            <Inbox />
          </Badge>
          <Badge value={42} size="sm">
            <Bell />
          </Badge>
          <Badge value={99}>★</Badge>
        </Stack>
      </Section>

      <Section title="Code example" hint="copy-pasteable usage">
        <Stack gap={5}>
          <CodeBlock
            language="tsx"
            title="Bare mount"
            code={`<Badge value={3} />
<Badge value={1500} />           // renders "99+"
<Badge value={1500} max={999} />  // renders "999+"`}
          />
          <CodeBlock
            language="tsx"
            title="In a button — wrap for clickability"
            code={`import { Button } from '@osd/ui/button';
import { Badge } from '@osd/ui/badge';
import { Inbox } from '@osd/icons/inbox';

<Button variant="ghost">
  <Inbox /> Inbox
  <Badge value={12} ariaLabel="12 unread messages" />
</Button>`}
          />
          <CodeBlock
            language="tsx"
            title="With a leading icon"
            code={`<Badge value={3}>
  <Inbox />
</Badge>`}
          />
        </Stack>
      </Section>

      <Section title="Accessibility" hint="Per-piece a11y guarantees">
        <Table columns="1fr 2fr 2fr">
          <Table.Header>
            <Table.Row>
              <Table.Head>Element</Table.Head>
              <Table.Head>Guarantee</Table.Head>
              <Table.Head>Notes</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>
                <code>&lt;Badge&gt;</code>
              </Table.Cell>
              <Table.Cell>
                Renders as <code>&lt;span&gt;</code>
              </Table.Cell>
              <Table.Cell>
                Non-interactive by default. For clickable cases, wrap in <code>&lt;button&gt;</code>{' '}
                or <code>&lt;Link&gt;</code>.
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <code>&lt;output&gt;</code> (the count)
              </Table.Cell>
              <Table.Cell>Semantically "the result of a computation"</Table.Cell>
              <Table.Cell>
                Better cross-device a11y than <code>role="status"</code> (per jsx-a11y). When{' '}
                <code>ariaLabel</code> is provided, it lands on the <code>&lt;output&gt;</code> so
                the whole badge (label + count) announces as a unit.
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Tabular numbers</Table.Cell>
              <Table.Cell>
                <code>font-variant-numeric: tabular-nums</code>
              </Table.Cell>
              <Table.Cell>
                Digits are monospaced — 1, 12, 99, 99+ all line up cleanly in a row (so a sidebar
                full of badges doesn't shift when counts change).
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Color</Table.Cell>
              <Table.Cell>No semantic color</Table.Cell>
              <Table.Cell>
                Badge is intentionally neutral. A red "12" doesn't mean "danger" — it just means
                "twelve things". For semantic color (success/danger/etc.) use{' '}
                <code>&lt;Pill&gt;</code>.
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </Section>
    </Stack>
  );
}
